import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { PaygapMetricItemDto } from "@green0meter/api-client";

import { getDefaultGraphConfigs } from "./helpers";
import { useChartData } from "./hooks";
import { BarChart } from "./components";

import { Card, CardContent } from "@components/base";

export interface PositionPayGapData {
  position: string;
  menAverage: number;
  womenAverage: number;
  payGap: number;
  description: string;
  employeeCount: number;
}

export interface GraphConfigTitle {
  cz: string;
  sk: string;
  en: string;
}

export interface GraphConfigSeriesItem {
  key: string;
  label: GraphConfigTitle;
}

export interface GraphConfig {
  graph_id: string;
  title: GraphConfigTitle;
  chart: string;
  x_axis: GraphConfigTitle;
  y_axis: GraphConfigTitle;
  series: GraphConfigSeriesItem[];
}

export interface ChartDimensions {
  barWidth: number;
  chartWidth: number;
}

export type LangKey = "cz" | "sk" | "en";

interface PaygapPayGradesOverviewProps {
  positionPayGapData?: PositionPayGapData[];
  graphConfigs?: GraphConfig[];
  analysisResults?: PaygapMetricItemDto[] | null;
}

const PaygapPayGradesOverview: FC<PaygapPayGradesOverviewProps> = ({
  positionPayGapData,
  graphConfigs = [],
  analysisResults,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language || "en";
  const langKey: LangKey =
    language === "cs" ? "cz" : language === "sk" ? "sk" : "en";

  // Use provided graphConfigs or fallback to default
  const defaultGraphConfigs = useMemo(() => getDefaultGraphConfigs(), []);
  const availableGraphConfigs: GraphConfig[] =
    graphConfigs && graphConfigs.length > 0 ? graphConfigs : defaultGraphConfigs;

  // Find the seniority_gap graph config or use the first one if available
  const defaultGraphId = useMemo(() => {
    return (
      availableGraphConfigs.find((g) => g.graph_id === "seniority_gap")?.graph_id ||
      availableGraphConfigs[0]?.graph_id ||
      ""
    );
  }, [availableGraphConfigs]);

  const [selectedGraphId, setSelectedGraphId] = useState<string>("");

  // Initialize selectedGraphId with defaultGraphId on mount and when it changes
  useEffect(() => {
    if (defaultGraphId && !selectedGraphId) {
      setSelectedGraphId(defaultGraphId);
    }
  }, [defaultGraphId, selectedGraphId]);

  // Ensure selectedGraphId is always valid when availableGraphConfigs changes
  useEffect(() => {
    if (availableGraphConfigs.length > 0 && defaultGraphId) {
      const isValidSelection = availableGraphConfigs.some(
        (g) => g.graph_id === selectedGraphId,
      );

      if (!isValidSelection) {
        setSelectedGraphId(defaultGraphId);
      }
    }
  }, [availableGraphConfigs, defaultGraphId, selectedGraphId]);

  // Get the selected graph configuration
  const selectedGraph = useMemo(() => {
    return (
      availableGraphConfigs.find((g) => g.graph_id === selectedGraphId) ||
      availableGraphConfigs[0]
    );
  }, [availableGraphConfigs, selectedGraphId]);

  // Extract chart data using custom hook
  const chartData = useChartData({
    selectedGraph,
    analysisResults,
    positionPayGapData,
  });

  // Get title from selected graph or fallback
  const chartTitle =
    selectedGraph?.title?.[langKey] || t("paygap.payGapPerPosition.title");
  const xAxisLabel =
    selectedGraph?.x_axis?.[langKey] || t("paygap.payGapPerPosition.positionLabel");
  const yAxisLabel =
    selectedGraph?.y_axis?.[langKey] || t("paygap.payGapPerPosition.payGapLabel");

  return (
    <Card sx={{ p: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">{chartTitle}</Typography>
          <Box display="flex" gap={2} alignItems="center">
            {/* Dropdown Select for Graph Selection */}
            {availableGraphConfigs.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 250 }}>
                <Select
                  value={selectedGraphId || defaultGraphId || ""}
                  onChange={(e) => {
                    const newValue = e.target.value as string;

                    if (newValue) {
                      setSelectedGraphId(newValue);
                    }
                  }}
                  displayEmpty
                  sx={{
                    fontSize: "0.875rem",
                    "& .MuiSelect-select": {
                      py: 0.5,
                    },
                  }}
                >
                  {availableGraphConfigs.map((graph) => (
                    <MenuItem key={graph.graph_id} value={graph.graph_id}>
                      {graph.title[langKey] || graph.title.en}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Tooltip title={t("paygap.payGapPerPosition.infoTooltip")}>
              <IconButton size="small">
                <Info />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box id="paygap-seniority-chart">
          <BarChart data={chartData} yAxisLabel={yAxisLabel} xAxisLabel={xAxisLabel} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default PaygapPayGradesOverview;
